import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAttendance, fetchAttendanceByDate } from "../../../redux/attendanceSlice";
import { fetchLmsEmployees } from "../../../redux/erpSlice";
import { fetchHolidaysForDate } from "../../../redux/holidaySlice";
import { useTranslation } from "react-i18next";
import { Calendar, Search, Filter, Download, Info } from "lucide-react";

const OverAllAttendance = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { attendanceRecords, status, error } = useSelector((state) => state.attendance);
  const { lmsEmployees, loading: employeesLoading } = useSelector((state) => state.lmsEmployees);
  const { holidaysForDate } = useSelector((state) => state.holidays);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filteredRecords, setFilteredRecords] = useState([]);

  useEffect(() => {
    dispatch(fetchAttendance());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  // Fetch attendance records when date changes
  useEffect(() => {
    dispatch(fetchAttendanceByDate(selectedDate));
    dispatch(fetchHolidaysForDate(selectedDate));
  }, [selectedDate, dispatch]);

  // Filter attendance records based on search term and status filter
  useEffect(() => {
    let filtered = attendanceRecords;

    // Filter by date if selected
    if (selectedDate) {
      filtered = filtered.filter(record => record.date === selectedDate);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(record => 
        record.teacherEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(record => 
        record.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredRecords(filtered);
  }, [attendanceRecords, selectedDate, searchTerm, statusFilter]);

  const getHolidayDisplay = (holidayInfo) => {
    if (!holidayInfo || holidayInfo.length === 0) return null;
    
    return (
      <div className="mt-1 text-xs text-purple-600 bg-purple-50 p-1 rounded">
        <div className="flex items-center">
          <span className="text-lg mr-1">🎉</span>
          <span className="font-medium">{holidayInfo.map(h => h.name).join(', ')}</span>
        </div>
        {holidayInfo[0]?.description && (
          <div className="mt-1 text-gray-600">
            {holidayInfo[0].description}
          </div>
        )}
      </div>
    );
  };

  const getEmployeeDetails = (employeeEmail) => {
    return lmsEmployees.find((emp) => emp.email === employeeEmail) || {};
  };

  const getLeaveReasonDisplay = (record) => {
    // Check if the record has leave information (this would be populated by the backend)
    if (record.leaveInfo) {
      return (
        <div className="mt-1 text-xs text-blue-600 bg-blue-50 p-1 rounded">
          <div className="flex items-center">
            <Info className="w-3 h-3 mr-1" />
            <span className="font-medium">{record.leaveInfo.leaveType}</span>
          </div>
          {record.leaveInfo.reason && (
            <div className="mt-1 text-gray-600">
              Reason: {record.leaveInfo.reason}
            </div>
          )}
        </div>
      );
    }
    
    // For records with "On Leave" status but no leaveInfo, show a generic message
    if (record.status === "On Leave") {
      return (
        <div className="mt-1 text-xs text-blue-600 bg-blue-50 p-1 rounded">
          <div className="flex items-center">
            <Info className="w-3 h-3 mr-1" />
            <span className="font-medium">On Leave</span>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleExportData = () => {
    const csvContent = [
      ["Employee Email", "Employee Name", "Date", "Check In", "Check Out", "Breaks", "Total Hours", "Status", "Leave Type", "Leave Reason"],
      ...filteredRecords.map(record => {
        const employee = getEmployeeDetails(record.teacherEmail);
        const breaks = record.breaks ? JSON.parse(record.breaks) : [];
        const breakText = breaks.map((b, i) => `Break ${i + 1}: ${b.breakIn || "N/A"} - ${b.breakOut || "N/A"}`).join(", ");
        
        return [
          record.teacherEmail,
          employee.name || "N/A",
          new Date(record.date).toLocaleDateString("en-GB"),
          record.checkIn || "N/A",
          record.checkOut || "N/A",
          breakText || "No breaks",
          record.totalHours || "N/A",
          record.status,
          record.leaveInfo?.leaveType || "N/A",
          record.leaveInfo?.reason || "N/A"
        ];
      })
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${selectedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const summary = {
    present: 0,
    late: 0,
    onLeave: 0,
    absent: 0,
  };

  filteredRecords.forEach((record) => {
    const status = record.status?.toLowerCase();
    if (status && summary.hasOwnProperty(status === "on leave" ? "onLeave" : status)) {
      summary[status === "on leave" ? "onLeave" : status]++;
    }
  });

  const isToday = selectedDate === new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-4 space-y-2 md:space-y-0">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                Overall Attendance
                <span className="text-gray-500 ml-2">({selectedDate})</span>
              </h2>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee email"
                value={searchTerm}
                onChange={handleSearchChange}
                className="border border-gray-300 rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="border border-gray-300 rounded-lg pl-10 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="absent">Absent</option>
                <option value="on leave">On Leave</option>
              </select>
            </div>
            <button
              onClick={handleExportData}
              className="bg-green-600 text-white rounded-lg px-4 py-2 text-sm flex items-center space-x-2 hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Present", count: summary.present, color: "bg-green-100 text-green-800" },
            { label: "Late", count: summary.late, color: "bg-yellow-100 text-yellow-800" },
            { label: "On Leave", count: summary.onLeave, color: "bg-blue-100 text-blue-800" },
            { label: "Absent", count: summary.absent, color: "bg-red-100 text-red-800" },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{item.label}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.color}`}>
                  {item.count}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Attendance Table */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Attendance Records</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100 text-left text-sm text-gray-700">
                  <th className="py-2 px-3">EMPLOYEE EMAIL</th>
                  <th className="px-3">EMPLOYEE NAME</th>
                  <th className="px-3">DATE</th>
                  <th className="px-3">CHECK IN</th>
                  <th className="px-3">CHECK OUT</th>
                  <th className="px-3">BREAKS</th>
                  <th className="px-3">TOTAL HOURS</th>
                  <th className="px-3">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {(status === "loading" || employeesLoading) && (
                  <tr>
                    <td colSpan="8" className="text-center py-4">Loading...</td>
                  </tr>
                )}
                {status === "failed" && (
                  <tr>
                    <td colSpan="8" className="text-center text-red-500">{error}</td>
                  </tr>
                )}
                {status === "succeeded" && filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">
                      No attendance records found for the selected criteria.
                    </td>
                  </tr>
                )}
                {status === "succeeded" &&
                  filteredRecords.map((record) => {
                    const employee = getEmployeeDetails(record.teacherEmail);
                    const breaks = record.breaks ? JSON.parse(record.breaks) : [];
                    const breakText = breaks.map((b, i) => `Break ${i + 1}: ${b.breakIn || "N/A"} - ${b.breakOut || "N/A"}`).join(", ");
                    const recordDate = new Date(record.date).toISOString().split("T")[0];
                    const isHoliday = holidaysForDate && holidaysForDate.length > 0 && 
                      holidaysForDate.some(h => {
                        const hStart = new Date(h.start).toISOString().split("T")[0];
                        const hEnd = new Date(h.end).toISOString().split("T")[0];
                        return recordDate >= hStart && recordDate <= hEnd;
                      });
                    const holidayInfo = isHoliday ? holidaysForDate.filter(h => {
                      const hStart = new Date(h.start).toISOString().split("T")[0];
                      const hEnd = new Date(h.end).toISOString().split("T")[0];
                      return recordDate >= hStart && recordDate <= hEnd;
                    }).map(h => ({ name: h.name, description: h.description })) : null;
                    
                    return (
                      <tr key={record.id} className="hover:bg-gray-200">
                        <td className="py-4 px-3">{record.teacherEmail}</td>
                        <td className="px-3">{employee.name || "N/A"}</td>
                        <td className="px-3">{new Date(record.date).toLocaleDateString("en-GB")}</td>
                        <td className="px-3">{record.checkIn || "N/A"}</td>
                        <td className="px-3">{record.checkOut || "N/A"}</td>
                        <td className="px-3">{breakText || "No breaks"}</td>
                        <td className="px-3">{record.totalHours || "N/A"}</td>
                        <td className="px-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            record.status === "Present" ? "bg-green-100 text-green-800" :
                            record.status === "Late" ? "bg-yellow-100 text-yellow-800" :
                            record.status === "On Leave" ? "bg-blue-100 text-blue-800" :
                            isHoliday ? "bg-purple-100 text-purple-800" :
                            "bg-red-100 text-red-800"
                          }`}>
                            {isHoliday ? "Holiday" : record.status}
                          </span>
                          {holidayInfo && getHolidayDisplay(holidayInfo)}
                          {getLeaveReasonDisplay(record)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverAllAttendance;